<template>
  <main class="invite">
    <div class="invite__card">
      <!-- The friend's "picture" is what the app shows too: initials on a
           gradient from the same palette (FriendAvatarView.swift). -->
      <div class="invite__avatar" :style="{ background: gradientCss }" aria-hidden="true">
        {{ initials }}
      </div>
      <h1 class="invite__title">{{ title }}</h1>
      <p class="invite__subtitle">{{ copy.subtitle }}</p>

      <div class="invite__stores">
        <a
          v-if="platform !== 'ios'"
          :href="playStoreUrl"
          class="store-link"
          @click="track('play')"
        >
          <img
            style="height: 73px; width: 186px; margin-right: -12px"
            src="./../assets/android.png"
            :alt="copy.download + ' Google Play'"
          />
        </a>
        <a
          v-if="platform !== 'android'"
          :href="appStoreUrl"
          class="store-link"
          @click="track('ios')"
        >
          <img
            style="height: 59px; width: 149px"
            src="./../assets/ios.svg"
            :alt="copy.download + ' App Store'"
          />
        </a>
      </div>

      <p class="invite__code">
        {{ copy.code }} <strong>{{ code }}</strong>
      </p>
    </div>
  </main>
</template>

<script>
import { trackEvent } from "../firebase";

// https://wellnotes.ai/invite/<CODE> for someone without the app. With
// the app installed, the universal link opens it and this never shows.
//
// Who invites comes from the page itself: `inviteLanding` (Cloud
// Function) reads the inviter's first name for the link preview and
// hands it over as window.__INVITE__. Without it (opened on the plain
// site), the page still works, only without the name.

// The 20 gradients FriendAvatarView.swift deals out, as RGB 0–255.
const GRADIENTS = [
  [[250, 112, 112], [209, 89, 51]],
  [[112, 250, 125], [51, 209, 105]],
  [[140, 112, 250], [122, 51, 209]],
  [[250, 153, 112], [209, 138, 51]],
  [[112, 250, 168], [51, 209, 153]],
  [[181, 112, 250], [168, 51, 209]],
  [[250, 194, 112], [209, 184, 51]],
  [[112, 250, 209], [51, 209, 199]],
  [[222, 112, 250], [209, 51, 204]],
  [[250, 237, 112], [186, 209, 51]],
  [[112, 250, 250], [51, 171, 209]],
  [[250, 112, 237], [209, 51, 156]],
  [[222, 250, 112], [140, 209, 51]],
  [[112, 209, 250], [51, 125, 209]],
  [[250, 112, 194], [209, 51, 110]],
  [[181, 250, 112], [92, 209, 51]],
  [[112, 168, 250], [51, 77, 209]],
  [[250, 112, 153], [209, 51, 61]],
  [[140, 250, 112], [51, 209, 59]],
  [[112, 125, 250], [74, 51, 209]],
];

/** The same stable djb2 hash the app uses, so a seed always picks the same colour. */
function hashSeed(seed) {
  let h = 5381;
  for (const byte of new TextEncoder().encode(seed)) h = (h * 33 + byte) | 0;
  return Math.abs(h);
}

export default {
  name: "InvitePage",
  data() {
    const injected = (typeof window !== "undefined" && window.__INVITE__) || {};
    const code = String(this.$route.params.code || injected.code || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 16);
    return {
      code,
      // Only trusted when it is about this code.
      name: injected.code === code && typeof injected.name === "string" ? injected.name : null,
    };
  },
  computed: {
    fr() {
      return String(this.$i18n?.locale || navigator.language || "").startsWith("fr");
    },
    copy() {
      return this.fr
        ? {
            invites: (n) => `${n} t'invite sur Wellnotes`,
            anonymous: "Tu es invité·e sur Wellnotes",
            subtitle:
              "Fiches, flashcards, quiz et cartes mentales générés par l'IA à partir de tes cours. Télécharge l'app et révisez ensemble !",
            download: "Télécharger sur",
            code: "Code d'invitation :",
          }
        : {
            invites: (n) => `${n} invited you to Wellnotes`,
            anonymous: "You're invited to Wellnotes",
            subtitle:
              "AI study notes, flashcards, quizzes and mind maps from your own courses. Get the app and study together!",
            download: "Download on",
            code: "Invite code:",
          };
    },
    title() {
      return this.name ? this.copy.invites(this.name) : this.copy.anonymous;
    },
    initials() {
      const words = (this.name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2);
      return words.length ? words.map((w) => w[0].toUpperCase()).join("") : "✍️";
    },
    gradientCss() {
      const [a, b] = GRADIENTS[hashSeed(this.code || "wellnotes") % GRADIENTS.length];
      return `linear-gradient(135deg, rgb(${a.join(",")}), rgb(${b.join(",")}))`;
    },
    /** Only the store that phone can install from; both on a computer. */
    platform() {
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
      if (/android/i.test(ua)) return "android";
      if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
      return "desktop";
    },
    appStoreUrl() {
      return this.$t("home.appStoreUrl");
    },
    playStoreUrl() {
      return "https://play.google.com/store/apps/details?id=com.antoine.cogito";
    },
  },
  mounted() {
    trackEvent?.("invite_landing_view", { has_name: Boolean(this.name), platform: this.platform });
  },
  methods: {
    track(store) {
      trackEvent?.("invite_landing_store_click", { store });
    },
  },
};
</script>

<style scoped>
.invite {
  min-height: calc(100vh - 220px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
}

.invite__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 440px;
}

.invite__avatar {
  width: 112px;
  height: 112px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 44px;
  font-weight: 700;
  font-family: ui-rounded, "SF Pro Rounded", system-ui, sans-serif;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  margin-bottom: 24px;
}

.invite__title {
  font-size: 28px;
  line-height: 1.2;
  font-weight: 700;
  margin: 0 0 12px;
}

.invite__subtitle {
  font-size: 16px;
  line-height: 1.5;
  opacity: 0.7;
  margin: 0 0 28px;
}

.invite__stores {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.store-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  transition: transform 160ms ease;
}

.store-link:hover {
  transform: translateY(-1px);
}

.invite__code {
  margin-top: 24px;
  font-size: 13px;
  opacity: 0.55;
}

.invite__code strong {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: 0.08em;
}
</style>
